import { Link } from 'react-router-dom'
import { PageHeader } from '../../shared/ui'

interface ToolCardProps {
  icon: string
  title: string
  description: string
  path: string
  badge?: string
  delay?: number
}

function ToolCard({ icon, title, description, path, badge, delay = 0 }: ToolCardProps) {
  return (
    <Link
      to={path}
      className="group relative overflow-hidden bg-white border border-slate-200/60 rounded-3xl p-6 flex items-center gap-5 transition-all duration-300 hover:border-primary/40 hover:shadow-xl hover:-translate-y-1 active:scale-[0.98] opacity-0 animate-[fade-in-up_0.6s_ease-out_forwards]"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      <div className="w-12 h-12 rounded-2xl bg-surface-container flex items-center justify-center flex-shrink-0 group-hover:bg-primary/10 transition-all duration-300">
        <span className="material-symbols-outlined text-on-surface-variant group-hover:text-primary transition-colors !text-[24px]">
          {icon}
        </span>
      </div>
      <div className="flex-1 min-w-0 z-10">
        <div className="flex items-center gap-2">
          <p className="font-bold text-on-surface group-hover:text-primary transition-colors text-[15px]">{title}</p>
          {badge && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-primary-container text-on-primary-container">
              {badge}
            </span>
          )}
        </div>
        <p className="text-[12px] text-on-surface-variant mt-0.5 leading-snug truncate font-medium">{description}</p>
      </div>
      <span className="material-symbols-outlined text-slate-200 ml-auto !text-[18px] group-hover:translate-x-1 group-hover:text-primary transition-all duration-300 z-10">
        chevron_right
      </span>
    </Link>
  )
}

export default function SupportDashboard() {
  return (
    <div className="max-w-5xl mx-auto space-y-10 animate-[fade-in_0.5s_ease-out]">
      <div className="opacity-0 animate-[fade-in-up_0.6s_ease-out_forwards]">
        <PageHeader
          title="Datenpflege & Schülerverwaltung"
          subtitle="Interne Tools für MegaTabelle, PCA-Portale und Allergie-Management"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
        <ToolCard
          icon="child_care"
          title="Schüler & PCA"
          description="Portalschnittstellen und Allergie-Management"
          path="/support/schueler"
          delay={175}
        />
      </div>

      {/* Info boxes */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div 
          className="bg-white border border-slate-200/60 rounded-3xl p-8 shadow-sm opacity-0 animate-[fade-in-up_0.6s_ease-out_forwards]"
          style={{ animationDelay: '300ms' }}
        >
          <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-6">Datenquellen</h4>
          <div className="space-y-4">
            {[
              { label: 'FileMaker ERP', icon: 'database', status: 'Online' },
              { label: 'MensaMax', icon: 'restaurant', status: 'External' },
              { label: 'PCA GmbH', icon: 'school', status: 'External' },
              { label: 'DIVIS (Gov)', icon: 'api', status: 'External' },
            ].map((src) => (
              <div key={src.label} className="flex items-center gap-4 text-sm group cursor-default">
                <span className="material-symbols-outlined text-primary/40 group-hover:text-primary transition-colors !text-[18px]">{src.icon}</span>
                <span className="text-on-surface font-medium">{src.label}</span>
                <span className="ml-auto text-[10px] font-bold text-teal-600 bg-teal-50 px-2 py-0.5 rounded-md uppercase tracking-wider">{src.status}</span>
              </div>
            ))}
          </div>
        </div>

        <div 
          className="bg-white border border-slate-200/60 rounded-3xl p-8 shadow-sm opacity-0 animate-[fade-in-up_0.6s_ease-out_forwards]"
          style={{ animationDelay: '400ms' }}
        >
          <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-6">Operational Notes</h4>
          <div className="space-y-4 text-[13px] text-on-surface-variant font-medium leading-relaxed">
            <p className="border-l-2 border-slate-100 pl-4 hover:border-primary transition-colors">
              <strong className="text-on-surface block text-[11px] uppercase tracking-wider mb-1">Aggregation</strong>
              Automated Go-scripts handle multi-source sync for GBS/GTS datasets.
            </p>
            <p className="border-l-2 border-slate-100 pl-4 hover:border-primary transition-colors">
              <strong className="text-on-surface block text-[11px] uppercase tracking-wider mb-1">DSGVO</strong>
              Article 9 sensitive data remains isolated from internal indexing.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
